import type { Lang } from '../i18n/langs';

/**
 * Texto de apoyo y preguntas frecuentes de cada página (SEO + ayuda al cliente).
 * Clave = ruta interna ("/cie"). Se muestra al final de la página (components/ServiceContent.tsx)
 * y genera el marcado FAQPage de schema.org.
 */
export interface PageContent {
  title?: string;
  intro?: string[];
  faqs: { q: string; a: string }[];
}

export const FAQ_TITLE: Record<Lang, string> = {
  es: 'Preguntas frecuentes',
  ca: 'Preguntes freqüents',
  en: 'Frequently asked questions',
  fr: 'Questions fréquentes',
};

export const SERVICE_CONTENT: Record<string, Record<Lang, PageContent>> = {
  "/": {
    "es": {
      "faqs": [
        {
          "q": "¿Atendéis urgencias eléctricas de noche y en festivos?",
          "a": "Sí. Tenemos un electricista de guardia las 24 horas, los 365 días del año, también noches, fines de semana y festivos. Llama al 600 23 82 83 y te decimos cuánto tardaremos; en Barcelona solemos llegar en menos de 30 minutos."
        },
        {
          "q": "¿En qué zonas trabajáis?",
          "a": "En Barcelona ciudad y en el área metropolitana: Barcelonès, Baix Llobregat y Garraf (por ejemplo L'Hospitalet, Badalona, Cornellà, Castelldefels, Gavà o Sitges). Si no ves tu municipio, llámanos y te lo confirmamos."
        },
        {
          "q": "¿Sois electricistas autorizados?",
          "a": "Sí. Somos instaladores eléctricos autorizados, con seguro de responsabilidad civil, y podemos emitir y tramitar el boletín eléctrico (CIE) y el BRIE."
        },
        {
          "q": "¿Qué hago si me quedo sin luz?",
          "a": "Mira primero si el corte es solo en tu casa o en todo el edificio o la calle. Si es solo tuyo, revisa el cuadro: si ha saltado el diferencial o un magnetotérmico, desenchufa los aparatos de esa zona y vuelve a subirlo. Si vuelve a saltar, huele a quemado o ves chispas, no insistas y llámanos. Si afecta a toda la zona, es una avería de la compañía distribuidora."
        }
      ]
    },
    "ca": {
      "faqs": [
        {
          "q": "Ateneu urgències elèctriques de nit i en festius?",
          "a": "Sí. Tenim un electricista de guàrdia les 24 hores, els 365 dies de l'any, també nits, caps de setmana i festius. Truca al 600 23 82 83 i et diem quant trigarem; a Barcelona solem arribar en menys de 30 minuts."
        },
        {
          "q": "En quines zones treballeu?",
          "a": "A Barcelona ciutat i a l'àrea metropolitana: Barcelonès, Baix Llobregat i Garraf (per exemple l'Hospitalet, Badalona, Cornellà, Castelldefels, Gavà o Sitges). Si no hi veus el teu municipi, truca'ns i t'ho confirmem."
        },
        {
          "q": "Sou electricistes autoritzats?",
          "a": "Sí. Som instal·ladors elèctrics autoritzats, amb assegurança de responsabilitat civil, i podem emetre i tramitar el butlletí elèctric (CIE) i el BRIE."
        },
        {
          "q": "Què faig si em quedo sense llum?",
          "a": "Mira primer si el tall és només a casa teva o a tot l'edifici o el carrer. Si és només teu, revisa el quadre: si ha saltat el diferencial o un magnetotèrmic, desendolla els aparells d'aquella zona i torna'l a pujar. Si torna a saltar, fa olor de cremat o veus espurnes, no hi insisteixis i truca'ns. Si afecta tota la zona, és una avaria de la companyia distribuïdora."
        }
      ]
    },
    "en": {
      "faqs": [
        {
          "q": "Do you handle electrical emergencies at night and on holidays?",
          "a": "Yes. We have an electrician on call 24 hours a day, 365 days a year, including nights, weekends and public holidays. Call +34 600 23 82 83 and we will tell you how long we need; in Barcelona we usually arrive in under 30 minutes."
        },
        {
          "q": "Which areas do you cover?",
          "a": "Barcelona city and the metropolitan area: Barcelonès, Baix Llobregat and Garraf (for example L'Hospitalet, Badalona, Cornellà, Castelldefels, Gavà or Sitges). If your town is not listed, call us and we will confirm."
        },
        {
          "q": "Are you licensed electricians?",
          "a": "Yes. We are authorised electrical installers with public liability insurance, and we can issue and register the electrical certificate (CIE) and the BRIE report."
        },
        {
          "q": "What should I do if the power goes out?",
          "a": "First check whether the outage affects only your home or the whole building or street. If it is only yours, look at the fuse box: if the RCD or a circuit breaker has tripped, unplug the appliances on that circuit and switch it back on. If it trips again, smells of burning or sparks, stop and call us. If the whole area is affected, it is a fault on the distribution company's network."
        }
      ]
    },
    "fr": {
      "faqs": [
        {
          "q": "Intervenez-vous la nuit et les jours fériés ?",
          "a": "Oui. Un électricien est de garde 24h/24, 365 jours par an, y compris la nuit, le week-end et les jours fériés. Appelez le +34 600 23 82 83 et nous vous indiquons le délai ; à Barcelone, nous arrivons généralement en moins de 30 minutes."
        },
        {
          "q": "Dans quelles zones intervenez-vous ?",
          "a": "À Barcelone et dans toute l'aire métropolitaine : Barcelonès, Baix Llobregat et Garraf (par exemple L'Hospitalet, Badalona, Cornellà, Castelldefels, Gavà ou Sitges). Si votre commune n'apparaît pas, appelez-nous pour vérifier."
        },
        {
          "q": "Êtes-vous des électriciens agréés ?",
          "a": "Oui. Nous sommes installateurs électriciens agréés, avec une assurance responsabilité civile, et nous pouvons établir et enregistrer le certificat électrique (CIE) et le rapport BRIE."
        },
        {
          "q": "Que faire en cas de coupure de courant ?",
          "a": "Vérifiez d'abord si la coupure concerne uniquement votre logement ou tout l'immeuble ou la rue. Si elle ne concerne que vous, regardez le tableau électrique : si le différentiel ou un disjoncteur a sauté, débranchez les appareils de ce circuit et réarmez-le. S'il saute à nouveau, s'il y a une odeur de brûlé ou des étincelles, n'insistez pas et appelez-nous. Si tout le quartier est touché, la panne vient du réseau du distributeur."
        }
      ]
    }
  },
  "/emergency-repairs": {
    "es": {
      "title": "Electricista de urgencias en Barcelona, 24 horas",
      "intro": [
        "Una avería eléctrica no espera. Si te has quedado sin luz, el diferencial salta una y otra vez, huele a quemado en un enchufe o hay cables a la vista, lo más seguro es que lo revise un profesional cuanto antes.",
        "Nuestro electricista de urgencias trabaja 24 horas en Barcelona y su área metropolitana: localiza el origen del fallo, deja la instalación en condiciones seguras y te explica qué ha pasado y qué conviene reparar."
      ],
      "faqs": [
        {
          "q": "¿Cuánto tardáis en llegar?",
          "a": "En Barcelona ciudad solemos llegar en menos de 30 minutos. En el resto del área metropolitana depende de la zona y del tráfico; al llamar te damos una hora aproximada."
        },
        {
          "q": "¿Por qué salta el diferencial?",
          "a": "Normalmente por una fuga de corriente: un electrodoméstico averiado, humedad en un enchufe o una caja de empalmes, o un cable dañado. El diferencial está para protegerte, así que no lo anules nunca. Si salta aunque desconectes todos los aparatos, hay que buscar la fuga en la instalación."
        },
        {
          "q": "¿Qué hago mientras llegáis?",
          "a": "Si hay olor a quemado, humo o chispas, baja el interruptor general del cuadro y no toques cables ni enchufes, y menos con las manos mojadas. Si hay fuego, no uses agua y llama al 112."
        },
        {
          "q": "¿Atendéis también comunidades, locales y oficinas?",
          "a": "Sí. Atendemos urgencias en viviendas, comunidades de vecinos, locales comerciales y oficinas."
        }
      ]
    },
    "ca": {
      "title": "Electricista d'urgències a Barcelona, 24 hores",
      "intro": [
        "Una avaria elèctrica no espera. Si t'has quedat sense llum, el diferencial salta una vegada i una altra, fa olor de cremat en un endoll o hi ha cables a la vista, el més segur és que ho revisi un professional com més aviat millor.",
        "El nostre electricista d'urgències treballa 24 hores a Barcelona i la seva àrea metropolitana: localitza l'origen de la fallada, deixa la instal·lació en condicions segures i t'explica què ha passat i què convé reparar."
      ],
      "faqs": [
        {
          "q": "Quant trigueu a arribar?",
          "a": "A Barcelona ciutat solem arribar en menys de 30 minuts. A la resta de l'àrea metropolitana depèn de la zona i del trànsit; quan truquis et donem una hora aproximada."
        },
        {
          "q": "Per què salta el diferencial?",
          "a": "Normalment per una fuita de corrent: un electrodomèstic avariat, humitat en un endoll o una caixa d'empalmaments, o un cable malmès. El diferencial hi és per protegir-te, així que no l'anul·lis mai. Si salta encara que desconnectis tots els aparells, cal buscar la fuita a la instal·lació."
        },
        {
          "q": "Què faig mentre arribeu?",
          "a": "Si fa olor de cremat, hi ha fum o espurnes, abaixa l'interruptor general del quadre i no toquis cables ni endolls, i encara menys amb les mans mullades. Si hi ha foc, no facis servir aigua i truca al 112."
        },
        {
          "q": "Ateneu també comunitats, locals i oficines?",
          "a": "Sí. Atenem urgències en habitatges, comunitats de veïns, locals comercials i oficines."
        }
      ]
    },
    "en": {
      "title": "Emergency electrician in Barcelona, 24/7",
      "intro": [
        "An electrical fault can't wait. If you have lost power, the RCD keeps tripping, a socket smells of burning or there are exposed wires, the safest thing is to have a professional check it as soon as possible.",
        "Our emergency electrician works 24 hours a day in Barcelona and the metropolitan area: we find the source of the fault, make the installation safe and explain what happened and what should be repaired."
      ],
      "faqs": [
        {
          "q": "How quickly can you arrive?",
          "a": "In Barcelona city we usually arrive in under 30 minutes. Elsewhere in the metropolitan area it depends on the location and traffic; when you call we give you an estimated time."
        },
        {
          "q": "Why does my RCD keep tripping?",
          "a": "Usually because of current leakage: a faulty appliance, damp in a socket or junction box, or a damaged cable. The RCD is there to protect you, so never bypass it. If it trips even with all appliances unplugged, the leak is in the installation and needs to be traced."
        },
        {
          "q": "What should I do while I wait?",
          "a": "If there is a burning smell, smoke or sparks, switch off the main switch in the fuse box and do not touch cables or sockets, especially with wet hands. If there is a fire, do not use water and call 112."
        },
        {
          "q": "Do you also work for residential buildings, shops and offices?",
          "a": "Yes. We handle emergencies in homes, residential communities, shops and offices."
        }
      ]
    },
    "fr": {
      "title": "Électricien d'urgence à Barcelone, 24h/24",
      "intro": [
        "Une panne électrique n'attend pas. Si vous êtes sans courant, si le différentiel saute sans arrêt, si une prise sent le brûlé ou si des câbles sont à nu, le plus sûr est de faire intervenir un professionnel au plus vite.",
        "Notre électricien d'urgence intervient 24h/24 à Barcelone et dans l'aire métropolitaine : il localise l'origine de la panne, sécurise l'installation et vous explique ce qui s'est passé et ce qu'il faut réparer."
      ],
      "faqs": [
        {
          "q": "En combien de temps arrivez-vous ?",
          "a": "À Barcelone, nous arrivons généralement en moins de 30 minutes. Ailleurs dans l'aire métropolitaine, cela dépend du secteur et de la circulation ; nous vous donnons une heure approximative au téléphone."
        },
        {
          "q": "Pourquoi mon différentiel saute-t-il ?",
          "a": "Le plus souvent à cause d'une fuite de courant : un appareil défectueux, de l'humidité dans une prise ou une boîte de dérivation, ou un câble endommagé. Le différentiel vous protège, ne le shuntez jamais. S'il saute même avec tous les appareils débranchés, il faut chercher la fuite dans l'installation."
        },
        {
          "q": "Que faire en attendant votre arrivée ?",
          "a": "En cas d'odeur de brûlé, de fumée ou d'étincelles, coupez l'interrupteur général du tableau et ne touchez ni câbles ni prises, surtout avec les mains mouillées. En cas d'incendie, n'utilisez pas d'eau et appelez le 112."
        },
        {
          "q": "Intervenez-vous aussi pour les copropriétés, commerces et bureaux ?",
          "a": "Oui. Nous intervenons en urgence dans les logements, les copropriétés, les commerces et les bureaux."
        }
      ]
    }
  },
  "/electrical-installations": {
    "es": {
      "title": "Instalaciones eléctricas nuevas y reformas en Barcelona",
      "intro": [
        "Hacemos instalaciones eléctricas nuevas y reformas en viviendas, locales y oficinas de Barcelona: desde cambiar el cuadro eléctrico o añadir circuitos hasta renovar todo el cableado de un piso antiguo.",
        "Trabajamos según el Reglamento Electrotécnico para Baja Tensión (REBT) y, cuando la obra lo requiere, tramitamos el boletín eléctrico (CIE) para que puedas contratar la luz o ampliar la potencia sin problemas."
      ],
      "faqs": [
        {
          "q": "¿Cuándo conviene renovar la instalación eléctrica?",
          "a": "Si la vivienda tiene muchos años, las protecciones saltan a menudo, faltan enchufes con toma de tierra o el cuadro no tiene diferencial, es buen momento para revisarla. También antes de reformar la cocina o el baño, o si vas a instalar aire acondicionado, placa de inducción o un cargador de coche eléctrico."
        },
        {
          "q": "¿Necesito boletín eléctrico después de la obra?",
          "a": "Depende del alcance. En instalaciones nuevas, reformas importantes o ampliaciones de potencia suele ser obligatorio. Te lo indicamos al ver la obra y, si hace falta, lo tramitamos nosotros."
        },
        {
          "q": "¿Instaláis puntos de recarga para coche eléctrico?",
          "a": "Sí, en garajes particulares y comunitarios. Revisamos antes si la instalación y la potencia contratada lo permiten."
        },
        {
          "q": "¿Cómo pido presupuesto?",
          "a": "Llámanos al 600 23 82 83. Según el trabajo, te pediremos unas fotos o haremos una visita para valorarlo."
        }
      ]
    },
    "ca": {
      "title": "Instal·lacions elèctriques noves i reformes a Barcelona",
      "intro": [
        "Fem instal·lacions elèctriques noves i reformes en habitatges, locals i oficines de Barcelona: des de canviar el quadre elèctric o afegir circuits fins a renovar tot el cablejat d'un pis antic.",
        "Treballem segons el Reglament Electrotècnic per a Baixa Tensió (REBT) i, quan l'obra ho requereix, tramitem el butlletí elèctric (CIE) perquè puguis contractar la llum o ampliar la potència sense problemes."
      ],
      "faqs": [
        {
          "q": "Quan convé renovar la instal·lació elèctrica?",
          "a": "Si l'habitatge té molts anys, les proteccions salten sovint, falten endolls amb presa de terra o el quadre no té diferencial, és un bon moment per revisar-la. També abans de reformar la cuina o el bany, o si hi vols instal·lar aire condicionat, placa d'inducció o un carregador de cotxe elèctric."
        },
        {
          "q": "Necessito butlletí elèctric després de l'obra?",
          "a": "Depèn de l'abast. En instal·lacions noves, reformes importants o ampliacions de potència sol ser obligatori. T'ho indiquem quan veiem l'obra i, si cal, el tramitem nosaltres."
        },
        {
          "q": "Instal·leu punts de recàrrega per a cotxe elèctric?",
          "a": "Sí, en garatges particulars i comunitaris. Abans revisem si la instal·lació i la potència contractada ho permeten."
        },
        {
          "q": "Com demano pressupost?",
          "a": "Truca'ns al 600 23 82 83. Segons la feina, et demanarem unes fotos o farem una visita per valorar-la."
        }
      ]
    },
    "en": {
      "title": "New electrical installations and renovations in Barcelona",
      "intro": [
        "We carry out new electrical installations and renovations in homes, shops and offices in Barcelona: from replacing the fuse box or adding circuits to rewiring an entire older flat.",
        "We work to the Spanish Low Voltage Electrical Regulations (REBT) and, when the job requires it, we register the electrical certificate (CIE) so you can set up your supply or increase your contracted power without problems."
      ],
      "faqs": [
        {
          "q": "When should I renew my electrical installation?",
          "a": "If the property is old, the breakers trip often, sockets have no earth or the fuse box has no RCD, it is a good time to have it checked. The same applies before renovating a kitchen or bathroom, or before adding air conditioning, an induction hob or an EV charger."
        },
        {
          "q": "Will I need an electrical certificate after the work?",
          "a": "It depends on the scope. New installations, major renovations and power increases usually require one. We will tell you when we see the job and, if needed, we handle it for you."
        },
        {
          "q": "Do you install electric vehicle chargers?",
          "a": "Yes, in private and shared garages. We first check whether the installation and your contracted power can support it."
        },
        {
          "q": "How do I get a quote?",
          "a": "Call us on +34 600 23 82 83. Depending on the job, we will ask for a few photos or visit to assess it."
        }
      ]
    },
    "fr": {
      "title": "Installations électriques neuves et rénovations à Barcelone",
      "intro": [
        "Nous réalisons des installations électriques neuves et des rénovations dans les logements, commerces et bureaux de Barcelone : du remplacement du tableau électrique ou de l'ajout de circuits jusqu'à la réfection complète du câblage d'un appartement ancien.",
        "Nous travaillons selon le règlement espagnol basse tension (REBT) et, lorsque les travaux l'exigent, nous enregistrons le certificat électrique (CIE) pour que vous puissiez ouvrir votre contrat ou augmenter votre puissance sans difficulté."
      ],
      "faqs": [
        {
          "q": "Quand faut-il rénover son installation électrique ?",
          "a": "Si le logement est ancien, si les protections sautent souvent, si des prises n'ont pas de terre ou si le tableau n'a pas de différentiel, c'est le bon moment pour la faire contrôler. De même avant de rénover une cuisine ou une salle de bain, ou d'ajouter une climatisation, une plaque à induction ou une borne de recharge."
        },
        {
          "q": "Aurai-je besoin d'un certificat électrique après les travaux ?",
          "a": "Cela dépend de leur ampleur. Les installations neuves, les rénovations importantes et les augmentations de puissance en exigent généralement un. Nous vous le précisons en voyant le chantier et, si nécessaire, nous nous en chargeons."
        },
        {
          "q": "Installez-vous des bornes de recharge pour voiture électrique ?",
          "a": "Oui, dans les garages privés et collectifs. Nous vérifions d'abord que l'installation et la puissance souscrite le permettent."
        },
        {
          "q": "Comment obtenir un devis ?",
          "a": "Appelez-nous au +34 600 23 82 83. Selon les travaux, nous vous demanderons quelques photos ou nous passerons les évaluer."
        }
      ]
    }
  },
  "/safety-inspections": {
    "es": {
      "title": "Revisión de la instalación eléctrica en Barcelona",
      "intro": [
        "Una revisión eléctrica detecta a tiempo los problemas que no se ven: conexiones flojas, cables recalentados, protecciones mal dimensionadas o falta de toma de tierra.",
        "Revisamos el cuadro, los circuitos, los enchufes y la puesta a tierra, medimos el aislamiento y comprobamos que diferenciales y magnetotérmicos funcionan. Al terminar recibes un informe con lo que hemos encontrado y las mejoras recomendadas."
      ],
      "faqs": [
        {
          "q": "¿Cada cuánto hay que revisar la instalación?",
          "a": "En una vivienda conviene revisarla cada pocos años, y siempre al comprar o alquilar un piso antiguo o si notas fallos. Algunos locales y garajes tienen además inspecciones periódicas obligatorias según el REBT."
        },
        {
          "q": "¿Qué incluye la revisión?",
          "a": "Inspección visual del cuadro y los circuitos, prueba de los diferenciales, medida de la toma de tierra y del aislamiento, comprobación de enchufes y puntos de luz, y un informe final."
        },
        {
          "q": "¿La revisión sirve como boletín eléctrico?",
          "a": "No, la revisión es un diagnóstico. Si necesitas un documento oficial, te indicamos si corresponde un CIE o un BRIE y lo tramitamos."
        },
        {
          "q": "¿Cuánto dura?",
          "a": "En un piso normal suele llevar unas pocas horas. En locales o edificios depende del tamaño de la instalación."
        }
      ]
    },
    "ca": {
      "title": "Revisió de la instal·lació elèctrica a Barcelona",
      "intro": [
        "Una revisió elèctrica detecta a temps els problemes que no es veuen: connexions fluixes, cables escalfats, proteccions mal dimensionades o manca de presa de terra.",
        "Revisem el quadre, els circuits, els endolls i la posada a terra, mesurem l'aïllament i comprovem que diferencials i magnetotèrmics funcionen. En acabar reps un informe amb el que hem trobat i les millores recomanades."
      ],
      "faqs": [
        {
          "q": "Cada quant cal revisar la instal·lació?",
          "a": "En un habitatge convé revisar-la cada pocs anys, i sempre que compris o lloguis un pis antic o notis fallades. Alguns locals i garatges tenen, a més, inspeccions periòdiques obligatòries segons el REBT."
        },
        {
          "q": "Què inclou la revisió?",
          "a": "Inspecció visual del quadre i els circuits, prova dels diferencials, mesura de la presa de terra i de l'aïllament, comprovació d'endolls i punts de llum, i un informe final."
        },
        {
          "q": "La revisió serveix com a butlletí elèctric?",
          "a": "No, la revisió és un diagnòstic. Si necessites un document oficial, t'indiquem si correspon un CIE o un BRIE i el tramitem."
        },
        {
          "q": "Quant dura?",
          "a": "En un pis normal sol portar unes poques hores. En locals o edificis depèn de la mida de la instal·lació."
        }
      ]
    },
    "en": {
      "title": "Electrical safety inspections in Barcelona",
      "intro": [
        "An electrical inspection finds hidden problems early: loose connections, overheating cables, wrongly sized protection devices or a missing earth.",
        "We check the fuse box, circuits, sockets and earthing, measure insulation resistance and test that RCDs and circuit breakers work. Afterwards you receive a report with our findings and recommended improvements."
      ],
      "faqs": [
        {
          "q": "How often should an installation be inspected?",
          "a": "For a home, every few years is advisable, and always when buying or renting an older property or if you notice faults. Some commercial premises and car parks also have mandatory periodic inspections under the REBT."
        },
        {
          "q": "What does the inspection include?",
          "a": "A visual check of the fuse box and circuits, RCD testing, earth and insulation measurements, a check of sockets and light points, and a final report."
        },
        {
          "q": "Does the inspection count as an electrical certificate?",
          "a": "No, the inspection is a diagnosis. If you need an official document, we will tell you whether a CIE or a BRIE applies and handle it for you."
        },
        {
          "q": "How long does it take?",
          "a": "For a typical flat it usually takes a few hours. For shops or buildings it depends on the size of the installation."
        }
      ]
    },
    "fr": {
      "title": "Contrôle de l'installation électrique à Barcelone",
      "intro": [
        "Un contrôle électrique détecte à temps les problèmes invisibles : connexions desserrées, câbles qui chauffent, protections mal dimensionnées ou absence de mise à la terre.",
        "Nous vérifions le tableau, les circuits, les prises et la mise à la terre, mesurons l'isolement et testons les différentiels et disjoncteurs. À la fin, vous recevez un rapport avec nos constats et les améliorations recommandées."
      ],
      "faqs": [
        {
          "q": "À quelle fréquence faut-il contrôler l'installation ?",
          "a": "Pour un logement, tous les quelques ans, et toujours lors de l'achat ou de la location d'un logement ancien, ou si vous constatez des anomalies. Certains commerces et parkings sont en outre soumis à des inspections périodiques obligatoires selon le REBT."
        },
        {
          "q": "Que comprend le contrôle ?",
          "a": "Une inspection visuelle du tableau et des circuits, le test des différentiels, la mesure de la terre et de l'isolement, la vérification des prises et points lumineux, et un rapport final."
        },
        {
          "q": "Le contrôle vaut-il certificat électrique ?",
          "a": "Non, il s'agit d'un diagnostic. Si vous avez besoin d'un document officiel, nous vous indiquons s'il faut un CIE ou un BRIE et nous nous en occupons."
        },
        {
          "q": "Combien de temps dure-t-il ?",
          "a": "Pour un appartement classique, quelques heures en général. Pour un commerce ou un immeuble, cela dépend de la taille de l'installation."
        }
      ]
    }
  },
  "/maintenance": {
    "es": {
      "title": "Mantenimiento eléctrico para comunidades, locales y viviendas",
      "intro": [
        "El mantenimiento eléctrico evita averías, cortes inesperados y consumos innecesarios. Ofrecemos mantenimiento preventivo, con revisiones programadas, y correctivo, para solucionar lo que falle.",
        "Trabajamos en comunidades de vecinos, locales comerciales, oficinas y viviendas de Barcelona, y adaptamos la frecuencia de las revisiones (trimestral, semestral o anual) al tipo de instalación y a su uso."
      ],
      "faqs": [
        {
          "q": "¿Qué diferencia hay entre mantenimiento preventivo y correctivo?",
          "a": "El preventivo revisa la instalación periódicamente para detectar problemas antes de que provoquen una avería. El correctivo repara lo que ya ha fallado."
        },
        {
          "q": "¿Qué plan de mantenimiento me conviene?",
          "a": "Para instalaciones con mucho uso, como restaurantes, comercios u oficinas, suele convenir una revisión trimestral o semestral. Para viviendas o comunidades pequeñas, normalmente basta con una revisión anual."
        },
        {
          "q": "¿Hacéis el mantenimiento de las zonas comunes de una comunidad?",
          "a": "Sí: cuadros generales, iluminación de escaleras y portal, garajes y demás instalaciones comunes."
        },
        {
          "q": "¿Y si hay una avería entre revisiones?",
          "a": "Tienes a tu disposición nuestro servicio de urgencias 24 horas, los 365 días del año."
        }
      ]
    },
    "ca": {
      "title": "Manteniment elèctric per a comunitats, locals i habitatges",
      "intro": [
        "El manteniment elèctric evita avaries, talls inesperats i consums innecessaris. Oferim manteniment preventiu, amb revisions programades, i correctiu, per solucionar el que falli.",
        "Treballem en comunitats de veïns, locals comercials, oficines i habitatges de Barcelona, i adaptem la freqüència de les revisions (trimestral, semestral o anual) al tipus d'instal·lació i al seu ús."
      ],
      "faqs": [
        {
          "q": "Quina diferència hi ha entre manteniment preventiu i correctiu?",
          "a": "El preventiu revisa la instal·lació periòdicament per detectar problemes abans que provoquin una avaria. El correctiu repara el que ja ha fallat."
        },
        {
          "q": "Quin pla de manteniment em convé?",
          "a": "Per a instal·lacions amb molt d'ús, com restaurants, comerços o oficines, sol convenir una revisió trimestral o semestral. Per a habitatges o comunitats petites, normalment n'hi ha prou amb una revisió anual."
        },
        {
          "q": "Feu el manteniment de les zones comunes d'una comunitat?",
          "a": "Sí: quadres generals, il·luminació d'escales i vestíbul, garatges i la resta d'instal·lacions comunes."
        },
        {
          "q": "I si hi ha una avaria entre revisions?",
          "a": "Tens a la teva disposició el nostre servei d'urgències 24 hores, els 365 dies de l'any."
        }
      ]
    },
    "en": {
      "title": "Electrical maintenance for buildings, shops and homes",
      "intro": [
        "Electrical maintenance prevents faults, unexpected power cuts and wasted energy. We offer preventive maintenance, with scheduled checks, and corrective maintenance to fix whatever fails.",
        "We work for residential communities, shops, offices and homes in Barcelona, and adapt the frequency of checks (quarterly, six-monthly or annual) to the type of installation and how it is used."
      ],
      "faqs": [
        {
          "q": "What is the difference between preventive and corrective maintenance?",
          "a": "Preventive maintenance checks the installation regularly to catch problems before they cause a fault. Corrective maintenance repairs what has already failed."
        },
        {
          "q": "Which maintenance plan do I need?",
          "a": "Heavily used installations such as restaurants, shops or offices usually benefit from quarterly or six-monthly checks. For homes or small buildings, an annual check is normally enough."
        },
        {
          "q": "Do you maintain the communal areas of a residential building?",
          "a": "Yes: main switchboards, stairwell and entrance lighting, car parks and other shared installations."
        },
        {
          "q": "What if something fails between checks?",
          "a": "Our 24-hour emergency service is available 365 days a year."
        }
      ]
    },
    "fr": {
      "title": "Maintenance électrique pour copropriétés, commerces et logements",
      "intro": [
        "La maintenance électrique évite les pannes, les coupures imprévues et les consommations inutiles. Nous proposons une maintenance préventive, avec des contrôles programmés, et corrective, pour réparer ce qui tombe en panne.",
        "Nous intervenons pour les copropriétés, commerces, bureaux et logements de Barcelone, et adaptons la fréquence des contrôles (trimestrielle, semestrielle ou annuelle) au type d'installation et à son usage."
      ],
      "faqs": [
        {
          "q": "Quelle différence entre maintenance préventive et corrective ?",
          "a": "La maintenance préventive contrôle régulièrement l'installation pour détecter les problèmes avant la panne. La maintenance corrective répare ce qui est déjà tombé en panne."
        },
        {
          "q": "Quel contrat de maintenance me convient ?",
          "a": "Pour les installations très sollicitées, comme les restaurants, commerces ou bureaux, un contrôle trimestriel ou semestriel est souvent conseillé. Pour un logement ou une petite copropriété, un contrôle annuel suffit généralement."
        },
        {
          "q": "Assurez-vous la maintenance des parties communes d'une copropriété ?",
          "a": "Oui : tableaux généraux, éclairage des escaliers et du hall, parkings et autres installations communes."
        },
        {
          "q": "Et en cas de panne entre deux contrôles ?",
          "a": "Notre service d'urgence 24h/24 est disponible 365 jours par an."
        }
      ]
    }
  },
  "/cie": {
    "es": {
      "title": "Boletín eléctrico (CIE) en Barcelona: qué es y cuándo lo necesitas",
      "intro": [
        "El CIE (Certificado de Instalación Eléctrica), conocido como boletín eléctrico o «butlletí», es el documento con el que un instalador autorizado acredita que una instalación cumple el Reglamento Electrotécnico para Baja Tensión (REBT). La compañía distribuidora lo pide para dar de alta la luz, para algunos aumentos de potencia y después de reformas importantes.",
        "Nos encargamos de todo el trámite: revisamos la instalación, hacemos las adecuaciones necesarias, emitimos el certificado y lo registramos ante la Generalitat de Catalunya."
      ],
      "faqs": [
        {
          "q": "¿Cuándo necesito un boletín eléctrico?",
          "a": "Para contratar la luz en una vivienda o local nuevo, cuando se hace una reforma importante o se cambia el uso de la instalación, y en aumentos de potencia que superan lo que admite la instalación actual. Si no lo tienes claro, llámanos y te decimos qué documento corresponde en tu caso."
        },
        {
          "q": "¿Qué datos necesito para pedirlo?",
          "a": "Los datos del titular, la dirección de la instalación y, si la tienes, una factura reciente de la luz. Con eso preparamos la visita."
        },
        {
          "q": "¿Cuánto se tarda en tenerlo?",
          "a": "Depende del estado de la instalación. Si ya cumple la normativa, el trámite es rápido; si hay que hacer adecuaciones, primero las hacemos. Te damos el plazo después de la visita."
        },
        {
          "q": "¿Qué diferencia hay entre el CIE y el BRIE?",
          "a": "El CIE certifica una instalación nueva o modificada. El BRIE acredita que una instalación existente, ya registrada, sigue en condiciones seguras. En algunos casos, como ciertos aumentos de potencia en viviendas, basta con un BRIE."
        }
      ]
    },
    "ca": {
      "title": "Butlletí elèctric (CIE) a Barcelona: què és i quan el necessites",
      "intro": [
        "El CIE (Certificat d'Instal·lació Elèctrica), conegut com a butlletí elèctric, és el document amb què un instal·lador autoritzat acredita que una instal·lació compleix el Reglament Electrotècnic per a Baixa Tensió (REBT). La companyia distribuïdora el demana per donar d'alta la llum, per a alguns augments de potència i després de reformes importants.",
        "Ens encarreguem de tot el tràmit: revisem la instal·lació, fem les adequacions necessàries, emetem el certificat i el registrem davant la Generalitat de Catalunya."
      ],
      "faqs": [
        {
          "q": "Quan necessito un butlletí elèctric?",
          "a": "Per contractar la llum en un habitatge o local nou, quan es fa una reforma important o es canvia l'ús de la instal·lació, i en augments de potència que superen el que admet la instal·lació actual. Si no ho tens clar, truca'ns i et diem quin document correspon en el teu cas."
        },
        {
          "q": "Quines dades necessito per demanar-lo?",
          "a": "Les dades del titular, l'adreça de la instal·lació i, si la tens, una factura recent de la llum. Amb això preparem la visita."
        },
        {
          "q": "Quant es triga a tenir-lo?",
          "a": "Depèn de l'estat de la instal·lació. Si ja compleix la normativa, el tràmit és ràpid; si cal fer adequacions, primer les fem. Et donem el termini després de la visita."
        },
        {
          "q": "Quina diferència hi ha entre el CIE i el BRIE?",
          "a": "El CIE certifica una instal·lació nova o modificada. El BRIE acredita que una instal·lació existent, ja registrada, continua en condicions segures. En alguns casos, com certs augments de potència en habitatges, n'hi ha prou amb un BRIE."
        }
      ]
    },
    "en": {
      "title": "Electrical certificate (CIE) in Barcelona: what it is and when you need it",
      "intro": [
        "The CIE (Certificado de Instalación Eléctrica), known in Spain as the «boletín eléctrico», is the document in which an authorised installer certifies that an installation complies with the Low Voltage Electrical Regulations (REBT). The distribution company asks for it to connect a new supply, for some power increases and after major renovations.",
        "We handle the whole process: we inspect the installation, carry out any necessary upgrades, issue the certificate and register it with the Generalitat de Catalunya."
      ],
      "faqs": [
        {
          "q": "When do I need an electrical certificate?",
          "a": "To set up electricity in a new home or commercial unit, after a major renovation or change of use, and for power increases beyond what the current installation allows. If you are unsure, call us and we will tell you which document applies to you."
        },
        {
          "q": "What information do I need to request it?",
          "a": "The account holder's details, the address of the installation and, if you have one, a recent electricity bill. With that we can prepare the visit."
        },
        {
          "q": "How long does it take?",
          "a": "It depends on the state of the installation. If it already complies, the process is quick; if upgrades are needed, we do those first. We give you a timeframe after the visit."
        },
        {
          "q": "What is the difference between a CIE and a BRIE?",
          "a": "The CIE certifies a new or modified installation. The BRIE confirms that an existing, already registered installation is still safe. In some cases, such as certain power increases in homes, a BRIE is enough."
        }
      ]
    },
    "fr": {
      "title": "Certificat électrique (CIE) à Barcelone : définition et cas où il est exigé",
      "intro": [
        "Le CIE (Certificado de Instalación Eléctrica), appelé en Espagne « boletín eléctrico », est le document par lequel un installateur agréé atteste qu'une installation est conforme au règlement basse tension (REBT). Le distributeur l'exige pour raccorder un nouveau compteur, pour certaines augmentations de puissance et après des travaux importants.",
        "Nous nous chargeons de toute la démarche : contrôle de l'installation, mise en conformité si nécessaire, établissement du certificat et enregistrement auprès de la Generalitat de Catalogne."
      ],
      "faqs": [
        {
          "q": "Quand ai-je besoin d'un certificat électrique ?",
          "a": "Pour ouvrir l'électricité dans un logement ou un local neuf, après une rénovation importante ou un changement d'usage, et pour les augmentations de puissance au-delà de ce que permet l'installation. En cas de doute, appelez-nous : nous vous dirons quel document s'applique."
        },
        {
          "q": "Quelles informations faut-il fournir ?",
          "a": "Les coordonnées du titulaire, l'adresse de l'installation et, si possible, une facture d'électricité récente. Cela nous permet de préparer la visite."
        },
        {
          "q": "Combien de temps faut-il ?",
          "a": "Cela dépend de l'état de l'installation. Si elle est déjà conforme, la démarche est rapide ; sinon, nous réalisons d'abord la mise en conformité. Nous vous donnons un délai après la visite."
        },
        {
          "q": "Quelle différence entre le CIE et le BRIE ?",
          "a": "Le CIE certifie une installation neuve ou modifiée. Le BRIE atteste qu'une installation existante, déjà enregistrée, reste sûre. Dans certains cas, comme certaines augmentations de puissance dans un logement, un BRIE suffit."
        }
      ]
    }
  },
  "/brie": {
    "es": {
      "title": "Boletín BRIE en Barcelona: reconocimiento de instalaciones existentes",
      "intro": [
        "El BRIE (Boletín de Reconocimiento de la Instalación Eléctrica) es un documento propio de Cataluña. Lo emite un instalador autorizado después de revisar una instalación existente, ya registrada, para acreditar que sigue en condiciones seguras de uso.",
        "Se utiliza sobre todo en viviendas y pequeñas instalaciones de hasta 20 kW: por ejemplo, para justificar un aumento de potencia moderado o cuando la distribuidora pide comprobar el estado de una instalación antigua. Revisamos la instalación, corregimos lo que haga falta y emitimos el BRIE."
      ],
      "faqs": [
        {
          "q": "¿Cuándo me piden el BRIE?",
          "a": "Habitualmente cuando la distribuidora pide acreditar que una instalación ya existente sigue siendo segura, por ejemplo en algunos aumentos de potencia en viviendas o tras la verificación de una instalación de más de 20 años. Llámanos y te confirmamos si en tu caso corresponde un BRIE o un CIE."
        },
        {
          "q": "¿Y si la instalación no pasa la revisión?",
          "a": "Te explicamos qué hay que adecuar y por qué. Hacemos las reparaciones necesarias y, cuando la instalación cumple, emitimos el documento."
        },
        {
          "q": "¿Qué necesito para pedirlo?",
          "a": "Los datos del titular, la dirección de la instalación y, si la tienes, una factura reciente de la luz."
        },
        {
          "q": "¿El BRIE sustituye al boletín eléctrico (CIE)?",
          "a": "No siempre. El BRIE sirve para instalaciones existentes que no se han modificado de forma importante; si la instalación es nueva o se reforma, hace falta un CIE."
        }
      ]
    },
    "ca": {
      "title": "Butlletí BRIE a Barcelona: reconeixement d'instal·lacions existents",
      "intro": [
        "El BRIE (Butlletí de Reconeixement de la Instal·lació Elèctrica) és un document propi de Catalunya. L'emet un instal·lador autoritzat després de revisar una instal·lació existent, ja registrada, per acreditar que continua en condicions segures d'ús.",
        "S'utilitza sobretot en habitatges i petites instal·lacions de fins a 20 kW: per exemple, per justificar un augment de potència moderat o quan la distribuïdora demana comprovar l'estat d'una instal·lació antiga. Revisem la instal·lació, corregim el que calgui i emetem el BRIE."
      ],
      "faqs": [
        {
          "q": "Quan em demanen el BRIE?",
          "a": "Habitualment quan la distribuïdora demana acreditar que una instal·lació ja existent continua sent segura, per exemple en alguns augments de potència en habitatges o després de la verificació d'una instal·lació de més de 20 anys. Truca'ns i et confirmem si en el teu cas correspon un BRIE o un CIE."
        },
        {
          "q": "I si la instal·lació no passa la revisió?",
          "a": "T'expliquem què cal adequar i per què. Fem les reparacions necessàries i, quan la instal·lació compleix, emetem el document."
        },
        {
          "q": "Què necessito per demanar-lo?",
          "a": "Les dades del titular, l'adreça de la instal·lació i, si la tens, una factura recent de la llum."
        },
        {
          "q": "El BRIE substitueix el butlletí elèctric (CIE)?",
          "a": "No sempre. El BRIE serveix per a instal·lacions existents que no s'han modificat de manera important; si la instal·lació és nova o es reforma, cal un CIE."
        }
      ]
    },
    "en": {
      "title": "BRIE report in Barcelona: assessment of existing installations",
      "intro": [
        "The BRIE (Boletín de Reconocimiento de la Instalación Eléctrica) is a document specific to Catalonia. An authorised installer issues it after inspecting an existing, already registered installation, to confirm that it is still safe to use.",
        "It is mainly used for homes and small installations of up to 20 kW: for example, to support a moderate power increase or when the distribution company asks for an older installation to be checked. We inspect the installation, fix anything needed and issue the BRIE."
      ],
      "faqs": [
        {
          "q": "When is a BRIE required?",
          "a": "Usually when the distribution company asks you to show that an existing installation is still safe, for example for some power increases in homes or after an inspection of an installation more than 20 years old. Call us and we will confirm whether you need a BRIE or a CIE."
        },
        {
          "q": "What if the installation fails the inspection?",
          "a": "We explain what needs to be upgraded and why, carry out the repairs and issue the document once the installation complies."
        },
        {
          "q": "What do I need to request it?",
          "a": "The account holder's details, the address of the installation and, if you have one, a recent electricity bill."
        },
        {
          "q": "Does a BRIE replace the electrical certificate (CIE)?",
          "a": "Not always. The BRIE covers existing installations that have not been significantly modified; a new or renovated installation needs a CIE."
        }
      ]
    },
    "fr": {
      "title": "Rapport BRIE à Barcelone : reconnaissance des installations existantes",
      "intro": [
        "Le BRIE (Boletín de Reconocimiento de la Instalación Eléctrica) est un document propre à la Catalogne. Un installateur agréé l'établit après avoir contrôlé une installation existante, déjà enregistrée, pour attester qu'elle reste sûre.",
        "Il concerne surtout les logements et petites installations jusqu'à 20 kW : par exemple pour justifier une augmentation de puissance modérée ou lorsque le distributeur demande de vérifier une installation ancienne. Nous contrôlons l'installation, corrigeons ce qui est nécessaire et établissons le BRIE."
      ],
      "faqs": [
        {
          "q": "Quand le BRIE est-il demandé ?",
          "a": "En général lorsque le distributeur demande de justifier qu'une installation existante est toujours sûre, par exemple pour certaines augmentations de puissance dans un logement ou après la vérification d'une installation de plus de 20 ans. Appelez-nous pour savoir s'il vous faut un BRIE ou un CIE."
        },
        {
          "q": "Et si l'installation n'est pas conforme ?",
          "a": "Nous vous expliquons ce qu'il faut mettre en conformité et pourquoi, réalisons les réparations et établissons le document une fois l'installation conforme."
        },
        {
          "q": "Que faut-il fournir ?",
          "a": "Les coordonnées du titulaire, l'adresse de l'installation et, si possible, une facture d'électricité récente."
        },
        {
          "q": "Le BRIE remplace-t-il le certificat électrique (CIE) ?",
          "a": "Pas toujours. Le BRIE vaut pour les installations existantes non modifiées de manière importante ; une installation neuve ou rénovée nécessite un CIE."
        }
      ]
    }
  }
};
